import { useEffect, useMemo, useState } from 'react';

import PaginationSection from '../../../common/PaginationSection';
import {
  adminPageUrl,
  checkboxColumn,
  marketColumn,
  orderDateColumn,
  ordererColumn,
  orderNoColumn,
  orderStatus,
  productNameColumn,
  productNoColumn,
  productOptionColumn,
  quantityColumn,
  salesAmountColumn,
} from '../orderTableColumnBlocks';

import EmptyFaceIcon from '@/assets/icons/EmptyFaceIcon.svg?react';
import { TooltipProvider } from '@/components/atoms/PcTooltip';
import { LayoutOrderColumn } from '@/pages/orders/_layouts/LayoutOrderColumn';
import { OrderItem } from '@/types/order-list';
import { deepEqual } from '@/utils/deep-equal';
import MarketTable from '../../../common/MarketTable';
import { useOrderDetails } from '../../OrderDetailsProvider';

interface Props {
  page: number;
  onChangePage: (page: number) => void;
  totalPages: number;
  orderList: OrderItem[];
}

const PAGE_SIZE = 10;

export function OrderTableColumn({ page, onChangePage, totalPages, orderList }: Props) {
  const { checkedOrders, setCheckedOrders, shippingCompanyData, shippingInfo, setShippingInfo } =
    useOrderDetails();

  useEffect(() => {
    setCheckedOrders([]);
  }, [orderList.length]);

  const handleCheck = (order: OrderItem, checked: boolean) => {
    setCheckedOrders((prev) =>
      checked ? [...prev, order] : prev.filter((item) => !deepEqual(item, order)),
    );
  };

  const selectableOrders = orderList.filter((order) => order.marketInfo.isOrderFurtherActionEnable);

  const allChecked =
    selectableOrders.length > 0 && checkedOrders.length === selectableOrders.length;

  const handleCheckAll = (checked: boolean) => {
    if (selectableOrders.length === 0) return; // 선택 가능한 주문이 없으면 아무 작업도 하지 않음
    setCheckedOrders(checked ? selectableOrders : []); // ✅ 전체 Order 객체 저장
  };

  const columns = useMemo(() => {
    const baseColumns = [
      checkboxColumn({ allChecked, handleCheckAll, checkedOrders, handleCheck }),
      orderNoColumn,
      orderStatus,
      marketColumn,
      orderDateColumn,
      productNameColumn,
      productOptionColumn,
      // carrierColumn은 shippingCompanyData가 비어있지 않을 때만 추가
      salesAmountColumn,
      quantityColumn,
      productNoColumn({ showSellerPageButton: true }),
      ordererColumn,
      adminPageUrl,
      // trackingNumberColumn(shippingInfo, setShippingInfo),
    ];

    return baseColumns;
  }, [
    // allChecked,
    // handleCheckAll,
    checkedOrders,
    handleCheck,
    shippingCompanyData,
    shippingInfo,
    setShippingInfo,
  ]);

  // columnOrder는 'checkbox'를 제외한 나머지 컬럼 id만 포함
  const [columnOrder, setColumnOrder] = useState(() =>
    columns.filter((c) => c.id !== 'checkbox').map((c) => c.id!),
  );

  return (
    <TooltipProvider>
      <LayoutOrderColumn className="relative">
        {/* MarketTable을 감싸는 div 추가 */}
        {/* <div className="min-h-0 overflow-x-auto overflow-y-scroll"> */}
        {/* <div> */}
        {/* ✅ 남은 공간 모두 차지 + 스크롤 */}
        <MarketTable
          orderList={orderList}
          columns={columns}
          columnOrder={columnOrder}
          setColumnOrder={setColumnOrder}
          // checkedOrders={checkedOrders}
          // setCheckedOrders={setCheckedOrders}
          pageIndex={page}
          pageSize={PAGE_SIZE}
          pageCount={Math.ceil(orderList.length / PAGE_SIZE)} // 클라이언트사이드 시
        />
        {orderList.length === 0 && (
          <div className="absolute inset-0 top-[40px] z-[20] flex flex-col items-center justify-center gap-[20px] rounded-[20px] bg-white">
            <EmptyFaceIcon />
            <p className="text-gr-500">검색결과가 없어요.</p>
          </div>
        )}
        {/* </div> */}
      </LayoutOrderColumn>

      {/* 페이지네이션 컨트롤 */}
      <div className="flex h-[70px] max-h-[70px] items-center justify-center gap-4 border-t">
        <PaginationSection page={page} onChangePage={onChangePage} totalPages={totalPages} />
      </div>
    </TooltipProvider>
  );
}

export default OrderTableColumn;
